/**
 * オーブの色（変数の識別子）
 */
export type OrbColor = "green" | "cyan" | "orange";

/**
 * 全オーブ色の一覧
 */
export const ORB_COLORS: readonly OrbColor[] = [
  "green",
  "cyan",
  "orange",
] as const;

/**
 * オーブ色の日本語表示名
 */
export const ORB_COLOR_LABELS: Record<OrbColor, string> = {
  green: "緑",
  cyan: "水色",
  orange: "オレンジ",
};

/**
 * 変数値の上限（オーバーフロー防止）
 */
export const VARIABLE_MAX_VALUE = 9999;

/**
 * 変数の状態を管理するクラス
 *
 * 3色のオーブで変数を表現する。
 * 実行開始時にリセットし、足す・かけるで値を操作する。
 * 値は -VARIABLE_MAX_VALUE 〜 VARIABLE_MAX_VALUE の範囲にクランプされる。
 */
export class VariableState {
  private values: Record<OrbColor, number>;

  constructor() {
    this.values = { green: 0, cyan: 0, orange: 0 };
  }

  /** 指定色の現在値を取得 */
  get(color: OrbColor): number {
    return this.values[color];
  }

  /** 全変数を0にリセット */
  reset(): void {
    this.values = { green: 0, cyan: 0, orange: 0 };
  }

  /** 指定色に値を足す（上限・下限でクランプ） */
  add(color: OrbColor, value: number): void {
    this.values[color] = this.clamp(this.values[color] + value);
  }

  /** 指定色に値をかける（上限・下限でクランプ） */
  multiply(color: OrbColor, value: number): void {
    const result = this.values[color] * value;
    this.values[color] = this.clamp(Number.isFinite(result) ? result : 0);
  }

  /** 値を上限・下限にクランプ */
  private clamp(value: number): number {
    return Math.min(Math.max(value, -VARIABLE_MAX_VALUE), VARIABLE_MAX_VALUE);
  }

  /** 全変数の現在値をコピーとして返す */
  getAll(): Record<OrbColor, number> {
    return { ...this.values };
  }
}
